import {
    createContext,
    ReactNode,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Hub } from "aws-amplify/utils";
import {
    AuthUser,
    getCurrentUser,
    signOut as amplifySignOut,
} from "aws-amplify/auth";
import {
    clearGoogleSignInRetry,
    failureMessageOf,
    isFirstSignInAfterLinkError,
    oauthErrorDescriptionFromUrl,
    retryGoogleSignInAfterLink,
} from "./federatedLinkRetry.ts";
import { describeOauthError, stripOauthParamsFromUrl } from "./oauthError.ts";
import { restoreReturnPath } from "./returnPath.ts";

export type AuthStatus = "configuring" | "authenticated" | "unauthenticated";

type AuthContextValue = {
    authStatus: AuthStatus;
    user: AuthUser | undefined;
    /** Re-read the current user from Cognito (e.g. after a headless sign-in). */
    refreshUser: () => Promise<void>;
    signOut: () => Promise<void>;
    /**
     * True while an OAuth redirect (Google) is being resolved after Cognito
     * bounces the browser back with `?code=`. Used to show a loading state
     * instead of flashing the sign-in screen.
     */
    isResolvingRedirect: boolean;
    /**
     * Why the last federated sign-in did not complete, ready to render. Cognito
     * reports a trigger's refusal here and nowhere else -- there is no failed
     * `signIn` call to catch -- so without this the browser simply arrives back
     * at the sign-in screen with no account and no explanation.
     */
    oauthError: string | null;
    /** Drop the message once the user starts another attempt. */
    clearOauthError: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const hasOAuthRedirectParams = () => {
    const params = new URLSearchParams(window.location.search);
    return params.has("code") && params.has("state");
};

/**
 * Cognito bounced us back refusing the first Google sign-in after linking the
 * identity to an invitee's pre-created account. We are about to send the
 * browser straight back to Google, so this counts as a redirect in flight.
 */
const hasRetryableOAuthError = () =>
    isFirstSignInAfterLinkError(oauthErrorDescriptionFromUrl());

export function AuthProvider({ children }: { children: ReactNode }) {
    const [authStatus, setAuthStatus] = useState<AuthStatus>("configuring");
    const [user, setUser] = useState<AuthUser | undefined>(undefined);
    const [isResolvingRedirect, setIsResolvingRedirect] = useState(
        () => hasOAuthRedirectParams() || hasRetryableOAuthError(),
    );
    const [oauthError, setOauthError] = useState<string | null>(null);

    const clearOauthError = useCallback(() => setOauthError(null), []);

    const refreshUser = useCallback(async () => {
        try {
            const currentUser = await getCurrentUser();
            setUser(currentUser);
            restoreReturnPath();
            setAuthStatus("authenticated");
        } catch {
            setUser(undefined);
            setAuthStatus("unauthenticated");
        }
    }, []);

    const signOut = useCallback(async () => {
        try {
            await amplifySignOut();
        } finally {
            setUser(undefined);
            setAuthStatus("unauthenticated");
        }
    }, []);

    useEffect(() => {
        // Amplify may have already processed (and rejected) the redirect
        // before this listener exists, so the URL is read directly as well.
        const settleFailure = (message: string | null) => {
            retryGoogleSignInAfterLink(message).then((retrying) => {
                if (retrying) {
                    return;
                }
                setOauthError(describeOauthError(message));
                stripOauthParamsFromUrl();
                setIsResolvingRedirect(false);
                setAuthStatus("unauthenticated");
            });
        };

        // Any `error_description` in the url, not only the retryable one. A
        // trigger's refusal arrives exactly here and, if Amplify settled the
        // redirect before this listener existed, nowhere else -- so reading only
        // the retryable case meant a deliberate, user-facing refusal put the
        // browser back on the sign-in screen saying nothing at all.
        const description = oauthErrorDescriptionFromUrl();

        if (description) {
            settleFailure(description);
        } else {
            // Resolve the initial session on mount.
            refreshUser();
        }

        const unsubscribe = Hub.listen("auth", ({ payload }) => {
            switch (payload.event) {
                case "signedIn":
                    clearGoogleSignInRetry();
                    setOauthError(null);
                    setUser(payload.data);
                    restoreReturnPath();
                    setAuthStatus("authenticated");
                    setIsResolvingRedirect(false);
                    break;
                case "signedOut":
                    setUser(undefined);
                    setAuthStatus("unauthenticated");
                    break;
                case "signInWithRedirect":
                    // OAuth code exchange succeeded; `signedIn` follows.
                    refreshUser();
                    break;
                case "signInWithRedirect_failure":
                    settleFailure(failureMessageOf(payload.data));
                    break;
                case "tokenRefresh_failure":
                    refreshUser();
                    break;
            }
        });

        return unsubscribe;
    }, [refreshUser]);

    const value = useMemo<AuthContextValue>(
        () => ({
            authStatus,
            user,
            refreshUser,
            signOut,
            isResolvingRedirect,
            oauthError,
            clearOauthError,
        }),
        [
            authStatus,
            user,
            refreshUser,
            signOut,
            isResolvingRedirect,
            oauthError,
            clearOauthError,
        ],
    );

    return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
    const ctx = use(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
}
